/* Collection product grid — filters sidebar / drawer, sort disclosure, AJAX re-render.
   One filter form serves both presentations: a persistent sidebar on desktop (live filtering)
   and a slide-in drawer below 990px or in drawer mode (staged: "Apply filters" submits).
   Results re-render through the Section Rendering API; the element's own markup is swapped in
   place, so listeners bound on the element survive every render. */

if (!customElements.get('collection-product-grid')) {
  class CollectionProductGrid extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      this.addEventListener('click', this.onClick, options);
      this.addEventListener('change', this.onChange, options);
      this.addEventListener('input', this.onInput, options);
      this.addEventListener('keydown', this.onKeydown, options);
      this.addEventListener('submit', this.onSubmit, options);
      document.addEventListener('click', this.onDocumentClick, options);
      window.addEventListener('popstate', this.onPopState, options);
      this.media = window.matchMedia('(min-width: 990px)');
      this.media.addEventListener('change', this.onMediaChange, options);
      this.appliedParams = this.currentQuery();
      this.initPrice();
    }

    disconnectedCallback() {
      if (this.lifecycle) this.lifecycle.abort();
      if (this.request) this.request.abort();
      window.clearTimeout(this.submitTimer);
      this.unlockScroll();
    }

    get motionReduced() {
      if (window.Theme && Theme.config && typeof Theme.config.motionReduced === 'boolean') {
        return Theme.config.motionReduced;
      }
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    get drawer() {
      return this.querySelector('[data-cpg-drawer]');
    }

    isDrawerMode() {
      return this.classList.contains('cpg--drawer') || !this.media.matches;
    }

    isDrawerOpen() {
      const drawer = this.drawer;
      return Boolean(drawer && drawer.classList.contains('is-open'));
    }

    /* ---- Query building ---- */

    currentQuery() {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.delete('section_id');
      return params.toString();
    }

    collectParams() {
      const params = new URLSearchParams();
      this.querySelectorAll('input[name]').forEach((input) => {
        if (input.disabled && !input.checked) return;
        if (input.type === 'hidden') {
          params.append(input.name, input.value);
          return;
        }
        if (input.type === 'checkbox' || input.type === 'radio') {
          if (!input.checked || input.value === '') return;
          if (input.name === 'sort_by' && input.value === this.dataset.defaultSort) return;
          params.append(input.name, input.value);
          return;
        }
        if (input.type === 'range') {
          const bound = input.dataset.cpgRange === 'min' ? 0 : Number(input.max);
          if (Number(input.value) === bound) return;
          params.append(input.name, input.value);
        }
      });
      return params.toString();
    }

    /* ---- Events ---- */

    onClick(event) {
      const target = event.target;

      const opener = target.closest('[data-cpg-open]');
      if (opener) {
        event.preventDefault();
        this.openDrawer(opener, opener.dataset.cpgOpen || '');
        return;
      }

      if (target.closest('[data-cpg-close]')) {
        event.preventDefault();
        this.closeDrawer(true);
        return;
      }

      const toggle = target.closest('.cpg__group-toggle');
      if (toggle) {
        event.preventDefault();
        this.toggleGroup(toggle);
        return;
      }

      const more = target.closest('[data-cpg-more]');
      if (more) {
        event.preventDefault();
        this.toggleMore(more);
        return;
      }

      const clear = target.closest('[data-cpg-clear]');
      if (clear) {
        event.preventDefault();
        const sort = this.querySelector('.cpg__sort-input:checked');
        const params = new URLSearchParams();
        if (sort && sort.value !== this.dataset.defaultSort) params.set('sort_by', sort.value);
        this.closeDrawer(false);
        this.render(params.toString());
        return;
      }

      const sortToggle = target.closest('[data-cpg-sort-toggle]');
      if (sortToggle) {
        event.preventDefault();
        this.toggleSort();
        return;
      }

      const pageLink = target.closest('[data-cpg-page]');
      if (pageLink) {
        event.preventDefault();
        const url = new URL(pageLink.href, window.location.origin);
        url.searchParams.delete('section_id');
        this.render(url.searchParams.toString(), { scroll: true, keepPage: true });
      }
    }

    onChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.name) return;

      if (input.classList.contains('cpg__sort-input')) {
        this.closeSort(true);
        this.render(this.isDrawerOpen() ? this.stagedWithSort(input.value) : this.collectParams());
        return;
      }

      if (input.type === 'range') this.syncPrice(input.closest('[data-cpg-price]'), input);

      if (this.isDrawerMode()) return;
      window.clearTimeout(this.submitTimer);
      this.submitTimer = window.setTimeout(() => this.render(this.collectParams()), 300);
    }

    /* Sorting from the toolbar while the drawer is open keeps applied filters, not staged ones */
    stagedWithSort(value) {
      const params = new URLSearchParams(this.appliedParams);
      if (value === this.dataset.defaultSort) params.delete('sort_by');
      else params.set('sort_by', value);
      return params.toString();
    }

    onInput(event) {
      const input = event.target;
      if (input.type === 'range') this.syncPrice(input.closest('[data-cpg-price]'), input);
    }

    onSubmit(event) {
      event.preventDefault();
      window.clearTimeout(this.submitTimer);
      this.closeDrawer(false);
      this.render(this.collectParams());
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.isSortOpen()) {
          event.stopPropagation();
          this.closeSort(true);
          return;
        }
        if (this.isDrawerOpen()) {
          event.stopPropagation();
          this.closeDrawer(true);
        }
        return;
      }

      if (event.key === 'Tab' && this.isDrawerOpen()) this.trapFocus(event);
    }

    onDocumentClick(event) {
      if (!this.isSortOpen()) return;
      const sort = this.querySelector('[data-cpg-sort]');
      if (sort && !sort.contains(event.target)) this.closeSort(false);
    }

    onPopState() {
      this.render(this.currentQuery(), { push: false, keepPage: true });
    }

    onMediaChange() {
      if (!this.isDrawerMode() && this.isDrawerOpen()) this.closeDrawer(false, true);
    }

    /* ---- Accordion groups ---- */

    toggleGroup(toggle, forceOpen) {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;
      const open = typeof forceOpen === 'boolean' ? forceOpen : toggle.getAttribute('aria-expanded') !== 'true';
      if (open === (toggle.getAttribute('aria-expanded') === 'true') && open === !panel.hidden) return;
      toggle.setAttribute('aria-expanded', String(open));

      const animate = window.Theme && Theme.motion && !this.motionReduced;
      if (open) {
        panel.hidden = false;
        if (animate) {
          Theme.motion.animate(
            panel,
            { opacity: [0, 1], transform: ['translateY(-4px)', 'translateY(0)'] },
            { duration: 0.3, easing: [0.25, 0.1, 0.25, 1] }
          );
        }
        return;
      }

      if (!animate) {
        panel.hidden = true;
        return;
      }
      Theme.motion
        .animate(panel, { opacity: [1, 0] }, { duration: 0.2, easing: 'ease' })
        .finished.then(() => {
          if (toggle.getAttribute('aria-expanded') !== 'true') panel.hidden = true;
        });
    }

    toggleMore(button) {
      const list = document.getElementById(button.getAttribute('aria-controls'));
      if (!list) return;
      const expand = button.getAttribute('aria-expanded') !== 'true';
      const extras = Array.from(list.querySelectorAll('[data-cpg-extra]'));
      extras.forEach((item) => {
        item.hidden = !expand;
      });
      button.setAttribute('aria-expanded', String(expand));
      button.textContent = expand ? button.dataset.labelLess : button.dataset.labelMore;
      if (expand && extras[0]) {
        const first = extras[0].querySelector('input');
        if (first && button.matches(':focus-visible')) first.focus();
      }
    }

    /* ---- Sort disclosure ---- */

    isSortOpen() {
      const toggle = this.querySelector('[data-cpg-sort-toggle]');
      return Boolean(toggle && toggle.getAttribute('aria-expanded') === 'true');
    }

    toggleSort() {
      if (this.isSortOpen()) {
        this.closeSort(false);
        return;
      }
      const toggle = this.querySelector('[data-cpg-sort-toggle]');
      const panel = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      if (window.Theme && Theme.motion && !this.motionReduced) {
        Theme.motion.animate(
          panel,
          { opacity: [0, 1], transform: ['translateY(-4px)', 'translateY(0)'] },
          { duration: 0.2, easing: [0.25, 0.1, 0.25, 1] }
        );
      }
      const checked = panel.querySelector('input:checked') || panel.querySelector('input');
      if (checked) checked.focus();
    }

    closeSort(restoreFocus) {
      const toggle = this.querySelector('[data-cpg-sort-toggle]');
      const panel = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
      if (!toggle || !panel) return;
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      if (restoreFocus) toggle.focus();
    }

    /* ---- Drawer ---- */

    openDrawer(opener, groupId) {
      const drawer = this.drawer;
      if (!drawer) return;
      this.closeSort(false);
      this.opener = opener;
      const panel = drawer.querySelector('[data-cpg-panel]');

      if (this.isDrawerMode()) {
        drawer.classList.add('is-open');
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        this.lockScroll();
        this.querySelectorAll('[data-cpg-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      }

      const group = groupId ? document.getElementById(groupId) : null;
      const groupToggle = group && group.querySelector('.cpg__group-toggle');
      if (groupToggle) this.toggleGroup(groupToggle, true);

      window.requestAnimationFrame(() => {
        if (group && panel) {
          panel.scrollTop = Math.max(0, group.offsetTop - 8);
        }
        const focusTarget = groupToggle || drawer.querySelector('.cpg__close') || panel;
        if (focusTarget) focusTarget.focus({ preventScroll: Boolean(group) });
      });
    }

    closeDrawer(restoreFocus, silent) {
      const drawer = this.drawer;
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      const panel = drawer.querySelector('[data-cpg-panel]');
      if (panel) {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
      }
      this.unlockScroll();
      this.querySelectorAll('[data-cpg-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (restoreFocus) {
        this.resetStaged();
        if (this.opener && this.opener.isConnected) this.opener.focus();
      }
      if (silent) this.resetStaged();
    }

    /* Closing without applying restores the server-rendered (applied) state */
    resetStaged() {
      this.querySelectorAll('[data-cpg-form] input[name]').forEach((input) => {
        if (input.type === 'checkbox' || input.type === 'radio') input.checked = input.defaultChecked;
        else input.value = input.defaultValue;
      });
      this.querySelectorAll('[data-cpg-price]').forEach((price) => this.syncPrice(price));
    }

    lockScroll() {
      if (this.scrollLocked) return;
      this.scrollLocked = true;
      document.body.classList.add('cpg-drawer-open');
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      this.scrollLocked = false;
      document.body.classList.remove('cpg-drawer-open');
    }

    trapFocus(event) {
      const panel = this.drawer && this.drawer.querySelector('[data-cpg-panel]');
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')
      ).filter((element) => element.offsetParent !== null || element.classList.contains('visually-hidden'));
      const visible = focusable.filter((element) => !element.closest('[hidden]'));
      if (!visible.length) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---- Price range ---- */

    initPrice() {
      this.querySelectorAll('[data-cpg-price]').forEach((price) => this.syncPrice(price));
    }

    syncPrice(price, moved) {
      if (!price) return;
      const min = price.querySelector('[data-cpg-range="min"]');
      const max = price.querySelector('[data-cpg-range="max"]');
      if (!min || !max) return;
      const top = Number(price.dataset.max) || 1;
      let lo = Number(min.value);
      let hi = Number(max.value);
      if (lo > hi) {
        if (moved === min) {
          lo = hi;
          min.value = String(lo);
        } else {
          hi = lo;
          max.value = String(hi);
        }
      }
      price.style.setProperty('--lo', String((lo / top) * 100));
      price.style.setProperty('--hi', String((hi / top) * 100));
      min.classList.toggle('is-front', lo >= top);
      const symbol = price.dataset.currency || '';
      const loLabel = price.querySelector('[data-cpg-price-label="min"]');
      const hiLabel = price.querySelector('[data-cpg-price-label="max"]');
      if (loLabel) loLabel.textContent = `${symbol}${lo}`;
      if (hiLabel) hiLabel.textContent = `${symbol}${hi}`;
      min.setAttribute('aria-valuetext', `${symbol}${lo}`);
      max.setAttribute('aria-valuetext', `${symbol}${hi}`);
    }

    /* ---- Rendering ---- */

    captureState() {
      const groups = {};
      this.querySelectorAll('.cpg__group-toggle').forEach((toggle) => {
        const group = toggle.closest('[data-cpg-group]');
        if (group) groups[group.id] = toggle.getAttribute('aria-expanded') === 'true';
      });
      const more = {};
      this.querySelectorAll('[data-cpg-more]').forEach((button) => {
        more[button.getAttribute('aria-controls')] = button.getAttribute('aria-expanded') === 'true';
      });
      const active = document.activeElement;
      return {
        groups,
        more,
        focusId: active && this.contains(active) && active.id ? active.id : null,
      };
    }

    restoreState(state) {
      Object.keys(state.groups).forEach((id) => {
        const group = document.getElementById(id);
        const toggle = group && group.querySelector('.cpg__group-toggle');
        const panel = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
        if (!toggle || !panel) return;
        toggle.setAttribute('aria-expanded', String(state.groups[id]));
        panel.hidden = !state.groups[id];
      });
      Object.keys(state.more).forEach((id) => {
        if (!state.more[id]) return;
        const button = this.querySelector(`[data-cpg-more][aria-controls="${CSS.escape(id)}"]`);
        if (button && button.getAttribute('aria-expanded') !== 'true') this.toggleMore(button);
      });
      if (state.focusId) {
        const element = document.getElementById(state.focusId);
        if (element && this.contains(element)) element.focus({ preventScroll: true });
      }
    }

    render(query, options = {}) {
      const push = options.push !== false;
      const params = new URLSearchParams(query);
      if (!options.keepPage) params.delete('page');
      const search = params.toString();

      if (this.request) this.request.abort();
      const controller = new AbortController();
      this.request = controller;
      this.classList.add('is-loading');
      const results = this.querySelector('.cpg__results');
      if (results) results.setAttribute('aria-busy', 'true');

      const sectionParams = new URLSearchParams(search);
      sectionParams.set('section_id', this.sectionId);
      const url = `${window.location.pathname}?${sectionParams.toString()}`;

      fetch(url, { signal: controller.signal, credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector(`collection-product-grid[data-section-id="${this.sectionId}"]`);
          if (!fresh) throw new Error('collection-product-grid: section markup missing');
          const state = this.captureState();
          this.innerHTML = fresh.innerHTML;
          this.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
          this.initPrice();
          this.restoreState(state);
          this.appliedParams = search;
          if (push) {
            window.history.pushState({ cpg: search }, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
          }
          const count = this.querySelector('.cpg__count');
          const status = this.querySelector('[data-cpg-status]');
          if (status && count) {
            const message = count.textContent.trim();
            window.setTimeout(() => {
              status.textContent = message;
            }, 60);
          }
          if (options.scroll) {
            const top = this.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top, behavior: this.motionReduced ? 'auto' : 'smooth' });
          }
          this.dispatchEvent(new CustomEvent('cpg:rendered', { bubbles: true }));
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          console.error(error);
          window.location.href = `${window.location.pathname}${search ? `?${search}` : ''}`;
        })
        .finally(() => {
          if (this.request !== controller) return;
          this.request = null;
          this.classList.remove('is-loading');
          const current = this.querySelector('.cpg__results');
          if (current) current.removeAttribute('aria-busy');
        });
    }
  }

  customElements.define('collection-product-grid', CollectionProductGrid);
}
